import { LANGUAGE_KEYS } from 'config/languageKeys.ts';

import { delayRetry } from 'utils/axios/nagra/utils.ts';
import debugLog from 'utils/debugLog.ts';
import isFunction from 'utils/function.ts';

type RetryCallback = () => Promise<void>;
type SubscriptionCallback = (visible: boolean) => void;
type VitalErrorDialogConfig = {
    actionButtonTitle: string;
    actionButtonOnPress: (() => Promise<void>) | (() => void);
    vitalDialogOnBackPress?: () => void;
};

interface RetryOptions {
    maxRetryCount: number;
    retryDuration: number;
}

const { RELOAD_BUTTON } = LANGUAGE_KEYS;

class RetryController {
    private _vitalDialogVisible: boolean = false;

    private _vitalDialogOnBackPress:
        | VitalErrorDialogConfig['vitalDialogOnBackPress']
        | undefined = undefined;

    private _vitalDialogActionButtonTitle = RELOAD_BUTTON;

    private _vitalDialogActionButtonOnPress:
        | (() => void)
        | (() => Promise<void>)
        | null = null;

    private _vitalDialogRetryCallback: RetryCallback | null = null;

    private _subscribers: Set<SubscriptionCallback> = new Set();

    private _retriesDone: number = 0;

    private _maxRetryCount: number = 5;

    private _retryDuration: number = 5 * 60 * 1000; // ms

    /**
     * Get the current visibility state of the vital dialog
     */
    get vitalDialogVisible(): boolean {
        return this._vitalDialogVisible;
    }

    /**
     * Set the visibility state of the vital dialog
     */
    set vitalDialogVisible(visible: boolean) {
        if (this._vitalDialogVisible !== visible) {
            this._vitalDialogVisible = visible;
            this._notifySubscribers();
        }
    }

    get vitalDialogActionButtonTitle(): string {
        return this._vitalDialogActionButtonTitle;
    }

    get vitalDialogOnBackPress() {
        return this._vitalDialogOnBackPress;
    }

    /**
     * Show the vital dialog
     */
    showVitalDialog({
        actionButtonTitle,
        actionButtonOnPress,
        vitalDialogOnBackPress,
    }: VitalErrorDialogConfig): void {
        this._vitalDialogOnBackPress = vitalDialogOnBackPress;
        this._vitalDialogActionButtonTitle = actionButtonTitle;
        this._vitalDialogActionButtonOnPress = actionButtonOnPress;
        this.vitalDialogVisible = true;
    }

    /**
     * Hide the vital dialog
     */
    hideVitalDialog(): void {
        this.vitalDialogVisible = false;
        this._resetRetry();
        this._vitalDialogRetryCallback = null;
        this._vitalDialogActionButtonOnPress = null;
    }

    /**
     * Register a retry callback function with configurable parameters
     * @param callback The function to call when retry is triggered
     * @param options Optional configuration for retry behavior
     */
    registerRetryCallback = (
        callback: RetryCallback,
        options?: Partial<RetryOptions>,
    ) => {
        if (!isFunction(callback)) {
            debugLog('RetryController', 'Invalid retry callback provided');

            return;
        }

        this._vitalDialogRetryCallback = callback;

        if (options) {
            if (typeof options.maxRetryCount === 'number') {
                this._maxRetryCount = options.maxRetryCount;
            }

            if (typeof options.retryDuration === 'number') {
                this._retryDuration = options.retryDuration;
            }
        }

        this._resetRetry();
    };

    async onVitalDialogActionButtonPress() {
        try {
            if (!isFunction(this._vitalDialogActionButtonOnPress)) {
                debugLog(
                    'RetryController',
                    'No onVitalDialogActionButtonPress callback registered',
                );
                return;
            }

            await this._vitalDialogActionButtonOnPress();
            this.hideVitalDialog();
        } catch (error) {
            debugLog(
                'RetryController',
                'Error executing onVitalDialogActionButtonPress callback',
                error,
            );
        }
    }

    /**
     * Execute the retry callback with the configured parameters
     */
    async executeRetry(): Promise<void> {
        if (!isFunction(this._vitalDialogRetryCallback)) {
            debugLog('RetryController', 'No retry callback registered');
            return;
        }

        if (this._retriesDone >= this._maxRetryCount) {
            debugLog('RetryController', 'Maximum retry attempts reached');
            this._resetRetry();
            return;
        }

        this._retriesDone += 1;
        debugLog(
            'RetryController',
            `Scheduling retry attempt ${this._retriesDone} of ${this._maxRetryCount}`,
        );

        try {
            await delayRetry(
                this._retriesDone,
                this._maxRetryCount,
                this._retryDuration,
            );

            if (isFunction(this._vitalDialogRetryCallback)) {
                await this._vitalDialogRetryCallback();
            }

            this.hideVitalDialog();

            return;
        } catch (error) {
            debugLog(
                'RetryController',
                'Error executing retry callback',
                error,
            );
        }

        // Schedule next retry if not reached max attempts
        if (this._retriesDone < this._maxRetryCount) {
            this.executeRetry();
        }
    }

    /**
     * Reset the retry counter and clear any pending retry timeouts
     */
    private _resetRetry(): void {
        debugLog('RetryController', '_resetRetry');
        this._retriesDone = 0;
    }

    /**
     * Subscribe to visibility changes
     * @param callback Function to call when visibility changes
     * @returns Unsubscribe function
     */
    subscribe(callback: SubscriptionCallback): () => void {
        if (!isFunction(callback)) {
            debugLog(
                'RetryController',
                'Invalid subscription callback provided',
            );
            return () => {};
        }

        this._subscribers.add(callback);

        // Immediately notify the subscriber of the current state
        callback(this._vitalDialogVisible);

        // Return unsubscribe function
        return () => {
            this._subscribers.delete(callback);
        };
    }

    /**
     * Notify all subscribers of visibility changes
     */
    private _notifySubscribers(): void {
        this._subscribers.forEach((callback) => {
            try {
                callback(this._vitalDialogVisible);
            } catch (error) {
                debugLog(
                    'RetryController',
                    'Error notifying subscriber',
                    error,
                );
            }
        });
    }
}

export default new RetryController();
